/**
 * ProjectEditDialog - Edit project metadata and notes
 * Stitch design system
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, Box, Typography, TextField, FormControl, Select, MenuItem, Autocomplete } from '@mui/material';
import { toast } from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { formatDateISO } from '../../utils/weekUtils';
import { PROJECT_STATUS_UITLEG } from '../../utils/taakStatus';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { afgeleidTeam } from './parts/afgeleidTeam';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
    {children}
  </Typography>
);

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem', fontSize: '0.82rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody,
    '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
    '&.Mui-focused fieldset': { borderColor: T.primary },
  },
  '& .MuiInputLabel-root': { fontFamily: T.fontBody, fontSize: '0.82rem' },
};

const statusOptions = ['Lead', 'Offerte', 'In progress', 'Afgerond', 'On Hold', 'Gearchiveerd', 'Geannuleerd', 'Vernietigd', 'Geen planning nodig'];

const statusColors = {
  'Lead': { bg: '#f3e8ff', text: '#7c3aed' }, 'Offerte': { bg: '#dbeafe', text: '#2563eb' },
  'In progress': { bg: '#dbeafe', text: '#1d4ed8' }, 'Afgerond': { bg: '#dcfce7', text: '#16a34a' },
  'On Hold': { bg: '#fef3c7', text: '#b45309' }, 'Gearchiveerd': { bg: T.surfaceLow, text: T.outline },
  'Geannuleerd': { bg: '#fee2e2', text: '#dc2626' },
};

const teamRoles = [
  { key: 'Projectleider', label: 'Projectleider', category: 'Productie' },
  { key: 'Creative', label: 'Creative', category: 'Productie' },
  { key: 'Productie', label: 'Productie', category: 'Productie' },
  { key: 'Cameraman', label: 'Cameraman', category: 'Post-productie' },
  { key: 'Editor', label: 'Editor', category: 'Post-productie' },
  { key: 'Animator', label: 'Animator', category: 'Post-productie' },
  { key: 'FrisseBlikker', label: 'Frisse blikker', category: 'Kwaliteit' },
  { key: 'Crew', label: 'Crew', category: 'Team' },
];

const DEADLINE_TYPES = ['Hard', 'Klant', 'Zacht', 'Intern'];

// Waar een voorstel vandaan komt (tooltip bij het afgeleide team).
const BRON_UITLEG = {
  draaidag: 'gekoppeld als crew op een draaidag',
  titel: 'staat in de titel van de Outlook-afspraak van een draaidag',
  rollup: 'uit de taken (Airtable-rollup op het project)',
  taken: 'uitvoerder van taken van dit project',
};
// Vaste lege lijst als standaard: een nieuwe [] per render liet het deadline-effect eindeloos lopen.
const GEEN = [];
const STATUS_KORT = { bevestigd: ' (b)', gevraagd: ' (g)', onzeker: ' (?)' };

function ProjectEditDialog({ open, onClose, project, notes = GEEN, deliverables = GEEN, tasks = GEEN, draaidagen = GEEN, onUpdate, onNotesUpdate, onDeliverableUpdate, onVeldBijgewerkt }) {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [personen, setPersonen] = useState([]);
  const [loadingPersonen, setLoadingPersonen] = useState(false);
  // Iedereen (ook freelancers): namen voor het afgeleide team en voor overgenomen externen.
  const [allePersonen, setAllePersonen] = useState([]);
  const [allePersonenGeladen, setAllePersonenGeladen] = useState(false);
  const [overnemen, setOvernemen] = useState(null);
  // Veld waarvoor een Overnemen/ongedaan maken loopt: zolang dicht, anders overschrijft de
  // response een handmatige wijziging die tijdens het wachten is gedaan (Codex r4).
  const [bezigVeld, setBezigVeld] = useState(null);
  // Laatste stand voor Overnemen/ongedaan maken: callbacks lopen na de PATCH, niet in de render.
  const formRef = useRef(null);
  const projectRef = useRef(project);
  const eigenProject = useRef(null);
  // Alle schrijfacties (Overnemen, ongedaan maken, Opslaan) na elkaar: elke actie bepaalt pas
  // als hij aan de beurt is welke lijst hij schrijft (Codex r2: twee snelle undo's overschreven elkaar).
  const wachtrij = useRef(Promise.resolve());
  const naElkaar = (fn) => {
    const klaar = wachtrij.current.then(fn, fn);
    wachtrij.current = klaar.catch(() => {});
    return klaar;
  };
  const [formData, setFormData] = useState({ Status: '', Projectleider: [], Creative: [], Productie: [], Cameraman: [], Editor: [], Animator: [], FrisseBlikker: [], Crew: [] });
  const [quickNoteText, setQuickNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Deadlines per deliverable — de vraag "kan hier de deadline aangepast worden?" (Joris, 9 sep).
  const [deadlineVorm, setDeadlineVorm] = useState({});
  const [savingDeadline, setSavingDeadline] = useState(null);

  // Bij (her)openen telt het eigen Overnemen niet meer: het formulier volgt het project weer.
  useEffect(() => { if (open) { eigenProject.current = null; loadPersonen(); } }, [open]);
  useEffect(() => {
    projectRef.current = project;
    // Ons eigen Overnemen werkt het project bij in de parent; dat mag het formulier (met
    // eventuele andere, nog niet opgeslagen wijzigingen) niet resetten.
    if (project && project === eigenProject.current) return;
    if (project) {
      const f = project.fields || {};
      setFormData({ Status: f.Status || 'In progress', Projectleider: f.Projectleider || [], Creative: f.Creative || [], Productie: f.Productie || [], Cameraman: f.Cameraman || [], Editor: f.Editor || [], Animator: f.Animator || [], FrisseBlikker: f.FrisseBlikker || [], Crew: f.Crew || [] });
      setIsEditing(true);
    }
  }, [project, open]);

  const loadPersonen = async () => {
    setLoadingPersonen(true);
    try { const r = await authFetch(`${BACKEND_URL}/Api/personen`); const d = await leesJson(r, 'Teamleden laden'); setPersonen(d.people || []); }
    catch (e) { toast.error(e.message || 'Kon teamleden niet laden'); }
    finally { setLoadingPersonen(false); }
    try { const r = await authFetch(`${BACKEND_URL}/Api/personen/all`); if (r.ok) { const d = await r.json(); setAllePersonen(d.people || []); setAllePersonenGeladen(true); } }
    catch { /* voorstellen vallen terug op de medewerkerslijst, zonder voornaam-match */ }
  };

  // Afgeleid team (23 okt 2026): voorstellen naast de handmatige velden, uit draaidag-crew,
  // Outlook-titel, rollups of taken. Overnemen = PATCH van alleen dat ene veld.
  const voorstellen = useMemo(() => afgeleidTeam({
    fields: { ...(project?.fields || {}), ...formData },
    tasks, draaidagen,
    personen: allePersonen.length ? allePersonen : personen,
    // Een titelnaam alleen op voornaam koppelen als de volledige lijst (met freelancers) er is,
    // anders lijkt een medewerker-Paul uniek terwijl er ook een freelancer-Paul bestaat.
    titelMatch: allePersonenGeladen,
  }), [project, formData, tasks, draaidagen, allePersonen, allePersonenGeladen, personen]);
  formRef.current = formData;

  const neemOver = async (role, item) => {
    if (!item.id || overnemen || saving || !project) return;
    const huidig = () => formRef.current?.[role.key] || [];
    const zetVeld = (bereken) => naElkaar(async () => {
      setBezigVeld(role.key);
      try {
      const waarde = bereken(huidig());
      const p = projectRef.current;
      const r = await authFetch(`${BACKEND_URL}/Api/projects/${p.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [role.key]: waarde }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.error === 'string' ? d.error : d.detail || d.error?.message || `HTTP ${r.status}`);
      setFormData(prev => ({ ...prev, [role.key]: waarde }));
      formRef.current = { ...(formRef.current || {}), [role.key]: waarde };
      const nieuw = { ...p, fields: { ...p.fields, [role.key]: waarde } };
      eigenProject.current = nieuw;
      projectRef.current = nieuw;   // synchroon: de volgende actie in de wachtrij bouwt hierop
      onVeldBijgewerkt?.(nieuw);
      } finally { setBezigVeld(null); }
    });
    setOvernemen(`${role.key}:${item.id}`);
    try {
      await zetVeld((h) => [...h.filter(x => x !== item.id), item.id]);
      // Ongedaan maken haalt alleen DEZE persoon weer weg; wat er intussen bij kwam blijft staan.
      biedOngedaanAan(`${item.naam} als ${role.label} overgenomen`,
        () => zetVeld((h) => h.filter(x => x !== item.id)), { eigenKnop: true });
    } catch (err) { toast.error(`Overnemen mislukt: ${err.message}`); }
    finally { setOvernemen(null); }
  };

  const renderVoorstellen = (role) => {
    const lijst = voorstellen[role.key];
    if (!lijst?.length) return null;
    return (
      <Box data-testid={`voorstel-${role.key}`} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: -0.75, mb: 1.5 }}>
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>Voorstel:</Typography>
        {lijst.map((item) => {
          const bezig = overnemen === `${role.key}:${item.id}`;
          return (
            <Box key={item.id || item.naam} title={BRON_UITLEG[item.bron] || ''} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.2, borderRadius: '9999px', border: `1px dashed ${T.outlineVariant}` }}>
              <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 600, color: T.secondary }}>
                {item.naam}{STATUS_KORT[item.status] || ''}
              </Typography>
              {item.id ? (
                <Box component="button" type="button" onClick={() => neemOver(role, item)} disabled={!!overnemen || saving}
                  sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: overnemen || saving ? 'default' : 'pointer', fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.primary, '&:hover': { textDecoration: 'underline' } }}>
                  {bezig ? 'Bezig…' : 'Overnemen'}
                </Box>
              ) : (
                <Typography component="span" title="Geen Personen-record — koppelen kan pas als deze persoon in Personen staat" sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant }}>
                  extern
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await naElkaar(async () => {
      // Na een lopende Overnemen/ongedaan maken: de actuele stand, niet die van deze render.
      const formData = formRef.current || {};
      const project = projectRef.current;
      const r = await authFetch(`${BACKEND_URL}/Api/projects/${project.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      const d = await r.json();
      if (!r.ok) { let msg = 'Kon project niet opslaan'; if (d.error) { msg = typeof d.error === 'string' ? d.error : d.error.message || d.error.error || JSON.stringify(d.error); } throw new Error(msg); }
      const updatedProject = { ...project, fields: { ...project.fields, ...formData, 'Projectleider (Volledige naam)': formData.Projectleider.map(id => personen.find(p => p.id === id)?.naam).filter(Boolean) } };
      projectRef.current = updatedProject;   // synchroon, vóór de wachtrij de volgende actie vrijgeeft
      onUpdate?.(updatedProject);
      setIsEditing(false);
      toast.success('Project opgeslagen');
      });
    } catch (err) { toast.error(`Fout bij opslaan: ${err.message}`); }
    finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!quickNoteText.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: project.id, title: quickNoteText.split('\n')[0].substring(0, 50) || 'Notitie', notes: quickNoteText, meetingDate: formatDateISO(new Date()), source: 'Manual' }) });
      if (!r.ok) throw new Error('Kon notitie niet opslaan');
      toast.success('Notitie toegevoegd');
      setQuickNoteText('');
      onNotesUpdate?.();
    } catch (err) { toast.error(`Fout: ${err.message}`); }
    finally { setSavingNote(false); }
  };

  const getPersonById = (id) => personen.find(p => p.id === id) || allePersonen.find(p => p.id === id);

  useEffect(() => {
    const vorm = {};
    (deliverables || []).forEach((d) => {
      vorm[d.id] = { Deadline: (d.fields?.Deadline || '').split('T')[0], TypeDeadline: d.fields?.TypeDeadline || '', 'Deadline Remarks': d.fields?.['Deadline Remarks'] || '' };
    });
    setDeadlineVorm(vorm);
  }, [deliverables]);

  const deadlineGewijzigd = (d) => {
    const v = deadlineVorm[d.id]; if (!v) return false;
    return v.Deadline !== (d.fields?.Deadline || '').split('T')[0] || v.TypeDeadline !== (d.fields?.TypeDeadline || '') || v['Deadline Remarks'] !== (d.fields?.['Deadline Remarks'] || '');
  };

  const bewaarDeadline = async (d) => {
    const v = deadlineVorm[d.id]; if (!v) return;
    setSavingDeadline(d.id);
    try {
      const body = { Deadline: v.Deadline || null, TypeDeadline: v.TypeDeadline || null, 'Deadline Remarks': v['Deadline Remarks'] };
      const r = await authFetch(`${BACKEND_URL}/Api/deliverables/${d.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.details?.error?.message || data.error || data.detail || 'Kon deadline niet opslaan');
      onDeliverableUpdate?.({ ...d, fields: { ...d.fields, ...body } });
      toast.success('Deadline opgeslagen');
    } catch (err) { toast.error(`Fout bij opslaan: ${err.message}`); }
    finally { setSavingDeadline(null); }
  };

  const renderTeamField = (role) => {
    const currentIds = formData[role.key] || [];
    const currentPersons = currentIds.map(id => getPersonById(id)).filter(Boolean);

    if (!isEditing) {
      const lookupField = `${role.key} (Volledige naam)`;
      const displayNames = project?.fields?.[lookupField] || project?.fields?.[`Naam (from ${role.key})`] || currentPersons.map(p => p.naam);
      return (
        <Box key={role.key} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mb: 0.5 }}>{role.label}</Typography>
          {displayNames && displayNames.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {displayNames.filter(Boolean).map((name, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '9999px', bgcolor: T.surfaceLow }}>
                  <Icon name="person" size={12} sx={{ color: T.outlineVariant }} />
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.text }}>{name}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontStyle: 'italic', color: T.outlineVariant }}>Niet toegewezen</Typography>
          )}
          <Box sx={{ mt: 1.5 }}>{renderVoorstellen(role)}</Box>
        </Box>
      );
    }

    return (
      <Box key={role.key}>
      <Autocomplete
        // Overgenomen freelancers staan niet in de medewerkerslijst; zonder deze aanvulling
        // verdwenen ze uit het veld zodra je het bewerkte.
        multiple options={[...personen, ...currentPersons.filter(p => !personen.some(q => q.id === p.id))]}
        disabled={bezigVeld === role.key}
        getOptionLabel={(o) => o.naam || ''}
        value={currentPersons}
        onChange={(_, sel) => setFormData(prev => ({ ...prev, [role.key]: sel.map(p => p.id) }))}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        loading={loadingPersonen}
        renderInput={(params) => <TextField {...params} label={role.label} size="small" placeholder={currentPersons.length === 0 ? 'Zoek teamlid...' : ''} sx={{ mb: 1.5, ...inputSx }} />}
        renderTags={(value, getTagProps) => value.map((person, index) => (
          <Box {...getTagProps({ index })} key={person.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '9999px', bgcolor: T.primaryContainer, mr: 0.5, mb: 0.5 }}>
            <Icon name="person" size={12} sx={{ color: T.primary }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: T.primary }}>{person.naam}</Typography>
          </Box>
        ))}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
            <Icon name="person" size={16} sx={{ color: T.outlineVariant }} />
            <Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text }}>{option.naam}</Typography>
              {option.functie && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outlineVariant }}>{option.functie}</Typography>}
            </Box>
          </Box>
        )}
      />
      {renderVoorstellen(role)}
      </Box>
    );
  };

  if (!project) return null;

  const projectName = project.fields?.Projectnr_en_naam || project.fields?.Projectnaam || 'Project';
  const currentStatus = project.fields?.Status || 'In progress';
  const st = statusColors[currentStatus] || statusColors['In progress'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '0.75rem' } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>{projectName}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
              <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: st.text, bgcolor: st.bg, px: 1.25, py: 0.3, borderRadius: '9999px' }}>
                {currentStatus}
              </Typography>
              {project.fields?.KlantnaamString && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary }}>{project.fields.KlantnaamString}</Typography>
              )}
            </Box>
          </Box>
          <Box onClick={onClose} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}>
            <Icon name="close" size={20} sx={{ color: T.outline }} />
          </Box>
        </Box>

        {/* Tab pills */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 2, mb: 0 }}>
          {[
            { label: 'Project Info', icon: 'person' },
            { label: `Notities (${notes.length})`, icon: 'note' },
            { label: `Deadlines (${deliverables.length})`, icon: 'event' },
          ].map((tab, i) => (
            <Box
              key={i}
              onClick={() => setActiveTab(i)}
              sx={{
                px: 1.5, py: 0.6, borderRadius: '0.5rem 0.5rem 0 0', cursor: 'pointer',
                bgcolor: activeTab === i ? T.surface : 'transparent',
                borderBottom: activeTab === i ? `2px solid ${T.tertiary}` : '2px solid transparent',
                display: 'flex', alignItems: 'center', gap: 0.5, transition: 'all 0.15s',
                '&:hover': activeTab !== i ? { bgcolor: T.surfaceLow } : {},
              }}
            >
              <Icon name={tab.icon} size={14} sx={{ color: activeTab === i ? T.tertiary : T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: activeTab === i ? 700 : 500, color: activeTab === i ? T.tertiary : T.outline }}>
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ height: 1, bgcolor: T.surfaceLow }} />

      {/* Content */}
      <Box sx={{ px: 3, py: 2.5, minHeight: 380, overflow: 'auto' }}>
        {/* Tab 0: Project Info */}
        {activeTab === 0 && (
          <>
            <Box sx={{ mb: 3 }}>
              <SectionLabel>Status</SectionLabel>
              {isEditing ? (
                <FormControl size="small" sx={{ minWidth: 200, ...inputSx }}>
                  <Select value={formData.Status} onChange={(e) => setFormData(prev => ({ ...prev, Status: e.target.value }))} sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>
                    {statusOptions.map(opt => (
                      <MenuItem key={opt} value={opt} sx={{ display: 'block' }}>
                        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', display: 'block' }}>{opt}</Typography>
                        {PROJECT_STATUS_UITLEG[opt] && <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', color: T.outline, display: 'block', whiteSpace: 'normal', maxWidth: 360 }}>{PROJECT_STATUS_UITLEG[opt]}</Typography>}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: (statusColors[formData.Status] || statusColors['In progress']).text, bgcolor: (statusColors[formData.Status] || statusColors['In progress']).bg, px: 1.25, py: 0.3, borderRadius: '9999px' }}>
                  {formData.Status}
                </Typography>
              )}
            </Box>

            <Box sx={{ height: 1, bgcolor: T.surfaceLow, mb: 2.5 }} />

            {['Productie', 'Post-productie', 'Kwaliteit', 'Team'].map(category => {
              const rolesInCategory = teamRoles.filter(r => r.category === category);
              return (
                <Box key={category} sx={{ mb: 2.5 }}>
                  <SectionLabel>{category}</SectionLabel>
                  <Box sx={{ display: 'grid', gridTemplateColumns: isEditing ? '1fr' : 'repeat(auto-fill, minmax(150px, 1fr))', gap: isEditing ? 0 : 1.5 }}>
                    {rolesInCategory.map(role => renderTeamField(role))}
                  </Box>
                </Box>
              );
            })}
          </>
        )}

        {/* Tab 1: Notes */}
        {activeTab === 1 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <TextField
                fullWidth size="small" placeholder="Typ een notitie en druk Enter..."
                value={quickNoteText} onChange={(e) => setQuickNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                disabled={savingNote} multiline maxRows={3}
                sx={inputSx}
              />
              <Box
                onClick={quickNoteText.trim() && !savingNote ? handleAddNote : undefined}
                sx={{
                  width: 36, height: 36, borderRadius: '0.5rem', flexShrink: 0, alignSelf: 'flex-start',
                  bgcolor: quickNoteText.trim() ? T.primaryContainer : T.surfaceLow,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: quickNoteText.trim() ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                }}
              >
                <Icon name={savingNote ? 'hourglass_empty' : 'add'} size={18} sx={{ color: quickNoteText.trim() ? T.primary : T.outlineVariant }} />
              </Box>
            </Box>

            {notes.length === 0 ? (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontStyle: 'italic', color: T.outlineVariant }}>
                Geen notities voor dit project
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {notes.map(note => {
                  const nf = note.fields || {};
                  const title = nf.Name || nf.Titel || 'Notitie';
                  const content = nf.Notities || nf.Notes || '';
                  const date = nf.Datum;
                  const source = nf.Bron;
                  return (
                    <Box key={note.id} sx={{ bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.75 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>{title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                          {source && (
                            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 600, color: T.outline, bgcolor: T.surface, px: 0.75, py: 0.15, borderRadius: '9999px' }}>
                              {source}
                            </Typography>
                          )}
                          {date && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outlineVariant }}>{new Date(date).toLocaleDateString('nl-NL')}</Typography>}
                        </Box>
                      </Box>
                      {content && (
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.secondary, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {content.length > 300 ? `${content.substring(0, 300)}...` : content}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {/* Tab 2: Deadlines per deliverable */}
        {activeTab === 2 && (
          deliverables.length === 0 ? (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontStyle: 'italic', color: T.outlineVariant }}>
              Dit project heeft nog geen deliverables — een deadline hangt altijd aan een deliverable.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
                Deadlines horen bij de deliverable, niet bij het project. Wijzig hier datum, type en toelichting; per regel opslaan.
              </Typography>
              {deliverables.map((d) => {
                const v = deadlineVorm[d.id] || { Deadline: '', TypeDeadline: '', 'Deadline Remarks': '' };
                const zet = (veld, waarde) => setDeadlineVorm(prev => ({ ...prev, [d.id]: { ...v, [veld]: waarde } }));
                const bezig = savingDeadline === d.id;
                return (
                  <Box key={d.id} sx={{ bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.5, display: 'grid', gridTemplateColumns: '1.4fr 150px 130px 1.4fr auto', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 700, color: T.text }}>{d.fields?.['Naam van Deliverable'] || 'Deliverable'}</Typography>
                    <TextField type="date" size="small" label="Deadline" InputLabelProps={{ shrink: true }} value={v.Deadline} onChange={(e) => zet('Deadline', e.target.value)} sx={inputSx} />
                    <FormControl size="small" sx={inputSx}>
                      <Select displayEmpty value={v.TypeDeadline} onChange={(e) => zet('TypeDeadline', e.target.value)} sx={{ fontFamily: T.fontBody, fontSize: '0.8rem' }}>
                        <MenuItem value=""><em>Type</em></MenuItem>
                        {DEADLINE_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Toelichting" value={v['Deadline Remarks']} onChange={(e) => zet('Deadline Remarks', e.target.value)} sx={inputSx} />
                    <Box onClick={deadlineGewijzigd(d) && !bezig ? () => bewaarDeadline(d) : undefined}
                      sx={{ px: 1.5, py: 0.6, borderRadius: '0.5rem', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap',
                        cursor: deadlineGewijzigd(d) && !bezig ? 'pointer' : 'default',
                        bgcolor: deadlineGewijzigd(d) ? T.primaryContainer : 'transparent', color: deadlineGewijzigd(d) ? T.primary : T.outlineVariant }}>
                      {bezig ? 'Bezig…' : 'Opslaan'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )
        )}
      </Box>

      {/* Actions */}
      {isEditing && activeTab === 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', px: 3, py: 2, borderTop: `1px solid ${T.surfaceLow}` }}>
          <Box onClick={() => setIsEditing(false)} sx={{ px: 2, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>
            Annuleren
          </Box>
          <Box
            onClick={!saving && !overnemen ? handleSave : undefined}
            sx={{
              px: 2.5, py: 0.75, borderRadius: '0.5rem', cursor: !saving ? 'pointer' : 'default',
              background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
              color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
              boxShadow: '0 4px 12px rgba(99,89,131,0.25)',
              display: 'flex', alignItems: 'center', gap: 0.5, opacity: saving ? 0.7 : 1,
              transition: 'all 0.2s',
              '&:hover': !saving ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } : {},
            }}
          >
            <Icon name="save" size={16} sx={{ color: '#fff' }} /> Opslaan
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

export default ProjectEditDialog;
